import React, { useCallback, useState } from "react";
import DadosEstadoCivilSantanderForm from "./DadosEstadoCivilSantanderForm";
import { useDispatch, useSelector } from "react-redux";
import {
  getPreenchimentoOperacao,
  isCarregandoConjuge,
  isGravandoPreenchimentoOperacao,
} from "../../../../selectors/operacao.selectors";
import useCompleteForm from "../../../../hooks/useCompleteForm";
import validators from "../../../../utils/validators";
import { actions } from "../../../../actions/operacao.actions";
import {
  removeNonDigitsFromString,
  transformFromDate,
  transformToDate,
} from "../../../../utils/basic";
import formatters from "../../../../utils/formatters";

const DadosEstadoCivilSantanderFormConnected = ({
  handleGoTo0,
  eventsNaoCorrentista,
  setComprador,
  comprador,
  ...props
}) => {
  const dispatch = useDispatch();
  const {
    operacao,
    dominios: {
      estadoCivil: estadosCivis,
      regimeComunhao: regimesComunhao,
      genero: generos,
    },
  } = useSelector(getPreenchimentoOperacao);
  const loadingConjuge = useSelector(isCarregandoConjuge);
  const submitting = useSelector(isGravandoPreenchimentoOperacao);

  const { conjuge, uniaoEstavel } =
    estadosCivis.find((e) => e.id === comprador?.estadoCivil?.id) ?? {};
  const [exibeConjuge, setExibeConjuge] = useState(
    (comprador?.uniaoEstavel || conjuge) ?? false
  );
  const [exibeUniaoEstavel, setExibeUniaoEstavel] = useState(
    uniaoEstavel ?? false
  );

  const callback = useCallback(
    (data) => {
      const c = data.operacao.participantes.find((p) => p.id === comprador.id);
      if (!!c) {
        setComprador({
          ...c,
          fluxo: data.validacao[
            data.operacao.detalhe.correntista ? "correntista" : "completa"
          ]?.participantes?.find((f) => f.id === c?.id)?.etapas,
        });
      }
      handleGoTo0();
    },
    [handleGoTo0, setComprador, comprador]
  );

  const [formProps, handleSubmit] = useCompleteForm({
    rules: (form) => ({
      estadoCivil: validators.object({ required: true }),
      dataCasamento: validators.date({
        min: "01/01/1900",
        max: formatters.dates.short(new Date()),
        custom: {
          requiredIf: (value) => {
            const uniaoEstavel = form.getValues("uniaoEstavel");

            if (!exibeConjuge && !uniaoEstavel) {
              return true;
            }

            if (value !== undefined && value !== null && value !== "")
              return true;
            return "Campo obrigatório";
          },
        },
      }),
      regimeComunhao: validators.object({
        custom: {
          requiredIf: (value) => {
            const uniaoEstavel = form.getValues("uniaoEstavel");

            if (!exibeConjuge && !uniaoEstavel) {
              return true;
            }

            if (value !== undefined && value !== null && value !== {})
              return true;
            return "Campo obrigatório";
          },
        },
      }),
      conjuge: {
        nome: validators.string({
          custom: {
            requiredIf: (value) => {
              const uniaoEstavel = form.getValues("uniaoEstavel");

              if (!exibeConjuge && !uniaoEstavel) {
                return true;
              }

              if (value !== undefined && value !== null && value !== "")
                return true;
              return "Campo obrigatório";
            },
            nomeCompleto: (value) =>
              !value ||
              (value.indexOf(" ") > 0 &&
                value.substring(value.indexOf(" ") + 1).length > 0) ||
              "Nome informado deve ser completo",
          },
        }),
        cpfCnpj: validators.cpf({
          custom: {
            requiredIf: (value) => {
              const uniaoEstavel = form.getValues("uniaoEstavel");

              if (!exibeConjuge && !uniaoEstavel) {
                return true;
              }

              if (value !== undefined && value !== null && value !== "")
                return true;
              return "Campo obrigatório";
            },
          },
        }),
        dataNascimento: validators.date({
          min: "01/01/1900",
          max: formatters.dates.short(new Date()),
          custom: {
            requiredIf: (value) => {
              const uniaoEstavel = form.getValues("uniaoEstavel");

              if (!exibeConjuge && !uniaoEstavel) {
                return true;
              }

              if (value !== undefined && value !== null && value !== "")
                return true;
              return "Campo obrigatório";
            },
          },
        }),
        genero: validators.object({
          custom: {
            requiredIf: (value) => {
              const uniaoEstavel = form.getValues("uniaoEstavel");

              if (!exibeConjuge && !uniaoEstavel) {
                return true;
              }

              if (value !== undefined && value !== null && value !== {})
                return true;
              return "Campo obrigatório";
            },
          },
        }),
      },
    }),
    initialValues: useCallback(
      () => ({
        estadoCivil: null,
        uniaoEstavel: false,
        regimeComunhao: null,
        dataCasamento: "",
        ...comprador,
        conjuge: {
          nome: "",
          cpfCnpj: "",
          dataNascimento: "",
          genero: null,
          ...comprador?.conjuge,
        },
      }),
      [comprador]
    ),
    handleSubmit: useCallback(
      (values) => {
        dispatch(
          actions.gravarPreenchimento.request({
            callback,
            instituicao: operacao.codigoInstituicao,
            passo: "dados-estado-civil",
            participante: {
              id: comprador?.id,
              idEstadoCivil: values.estadoCivil?.id,
              uniaoEstavel: exibeUniaoEstavel && values.uniaoEstavel,
              dataCasamento: !!exibeConjuge
                ? transformToDate(values.dataCasamento)
                : null,
              idRegimeComunhao: !!exibeConjuge
                ? values.regimeComunhao?.id
                : null,
              conjuge: !!exibeConjuge
                ? {
                    nome: values.conjuge.nome,
                    cpfCnpj: removeNonDigitsFromString(values.conjuge.cpfCnpj),
                    dataNascimento: transformToDate(
                      values.conjuge.dataNascimento
                    ),
                    idGenero: values.conjuge.genero?.id,
                  }
                : null,
            },
            operacao: {
              id: operacao.id,
              idParceiro: operacao.idParceiro,
            },
          })
        );
      },
      [dispatch, comprador, callback, operacao, exibeConjuge, exibeUniaoEstavel]
    ),
  });
  const { setValue } = formProps;

  const handleEstadoCivilChange = useCallback(
    (v) => {
      const vaiMudar =
        !exibeConjuge && !exibeUniaoEstavel
          ? true
          : exibeUniaoEstavel ^ (v?.uniaoEstavel ?? false);

      if (vaiMudar) {
        setValue("uniaoEstavel", false);
        setExibeUniaoEstavel(v?.uniaoEstavel ?? false);
        setExibeConjuge(v?.conjuge ?? false);
      }
    },
    [
      setExibeConjuge,
      setExibeUniaoEstavel,
      exibeUniaoEstavel,
      exibeConjuge,
      setValue,
    ]
  );
  const handleUniaoEstavelChange = useCallback(
    (e) => {
      setExibeConjuge(e.target.checked);
    },
    [setExibeConjuge]
  );

  const successCallback = useCallback(
    (data) => {
      if (!!data) {
        setValue("conjuge.nome", data.nome);
        setValue(
          "conjuge.dataNascimento",
          transformFromDate(data.dataNascimento)
        );
        if (!!data.genero) {
          setValue("conjuge.genero", { id: data.genero });
        } else {
          setValue("conjuge.genero", null);
        }
      }
    },
    [setValue]
  );
  const handleCpfCnpjChange = useCallback(
    (v) => {
      if (!!v) {
        dispatch(
          actions.obterConjuge.request({
            cpfCnpj: removeNonDigitsFromString(v),
            parceiro: operacao.idParceiro,
            callback: successCallback,
          })
        );
      }
    },
    [dispatch, operacao, successCallback]
  );

  return (
    <DadosEstadoCivilSantanderForm
      {...props}
      estadosCivis={estadosCivis}
      regimesComunhao={regimesComunhao}
      generos={generos}
      events={eventsNaoCorrentista}
      fluxo={comprador.fluxo}
      formProps={formProps}
      handleEstadoCivilChange={handleEstadoCivilChange}
      handleSubmit={handleSubmit}
      exibeConjuge={exibeConjuge}
      exibeUniaoEstavel={exibeUniaoEstavel}
      handleUniaoEstavelChange={handleUniaoEstavelChange}
      submitting={submitting}
      loadingConjuge={loadingConjuge}
      handleCpfCnpjChange={handleCpfCnpjChange}
    />
  );
};

export default DadosEstadoCivilSantanderFormConnected;
